import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { Box, Button, Stack } from "@mui/material";
import type { FundraisingStatus } from "@entrilia/shared/api/fundraisingTypes";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import VisibilityIcon from "@entrilia/shared/icons/VisibleIcon";
import { fundraisingStatusCaptionMap } from "../../../../utilities/enumCaptions";
import type { FundraisingDocumentsFilterValue } from "./fundraisingDocumentsFiltering";

interface Props {
  categoryOptions: SelectOption[];
  fundOptions?: SelectOption[];
  investorOptions?: SelectOption[];
  filters: FundraisingDocumentsFilterValue;
  onChange: (newFilters: FundraisingDocumentsFilterValue) => void;
  onClear: () => void;
  isLoading?: boolean;
  recordsCount: number;
  totalRecordsCount: number;
}

interface SelectOption {
  value: string;
  label: string;
}

const statusOptions = Object.entries(fundraisingStatusCaptionMap).map(([value, label]) => ({ value, label }));

const FundraisingDocumentFilters = ({
  categoryOptions,
  fundOptions,
  investorOptions,
  filters,
  onChange,
  onClear,
  isLoading,
  recordsCount,
  totalRecordsCount,
}: Props) => {
  const handleSearch = (searchTerm: string) => onChange({ ...filters, searchTerm });

  const handleInvestorsChange = (investorIds: string[]) => onChange({ ...filters, investorIds });

  const handleFundsChange = (fundIds: string[]) => onChange({ ...filters, fundIds });

  const handleCategoriesChange = (categoryIds: string[]) => onChange({ ...filters, categoryIds });

  const handleStatusesChange = (statuses: FundraisingStatus[]) => onChange({ ...filters, statuses });

  const showClearButton =
    filters.categoryIds.length + filters.fundIds.length + filters.investorIds.length + filters.statuses.length > 0;

  return (
    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mr: 1 }}>
        <Box sx={{ width: "8rem" }}>
          <RecordCounter records={recordsCount} totalRecords={totalRecordsCount} />
        </Box>
        {investorOptions && (
          <MultiSelectFilter
            sortOptions
            placeholder="Investor"
            searchable
            options={investorOptions}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            selectedValues={filters.investorIds}
            onChange={handleInvestorsChange}
            StartIcon={PersonIcon}
            disabled={isLoading}
          />
        )}
        {fundOptions && (
          <MultiSelectFilter
            sortOptions
            placeholder="Fund"
            searchable
            options={fundOptions}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            selectedValues={filters.fundIds}
            onChange={handleFundsChange}
            StartIcon={CompaniesIcon}
            disabled={isLoading}
          />
        )}
        <MultiSelectFilter
          placeholder="Category"
          options={categoryOptions}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          selectedValues={filters.categoryIds}
          onChange={handleCategoriesChange}
          StartIcon={DescriptionIcon}
          disabled={isLoading}
        />
        <MultiSelectFilter
          placeholder="Status"
          options={statusOptions}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          selectedValues={filters.statuses}
          onChange={handleStatusesChange}
          StartIcon={VisibilityIcon}
          disabled={isLoading}
        />
        {showClearButton && (
          <Button variant="text" onClick={onClear}>
            Clear all
          </Button>
        )}
      </Stack>
      <SearchField onSearch={handleSearch} debounceTimeMs={500} />
    </Stack>
  );
};

export default FundraisingDocumentFilters;
